import { ChangeEvent, DragEvent, FC, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardOnboardingDocument } from '../../../../queries/pxtraCard';
import { CheckmarkIcon } from '../../../icons';

type Props = {
  document: CardOnboardingDocument;
  disabled: boolean;
  onUpload: (documentType: string, file: File) => void;
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Swan names purposes and types in its own English. The catalogue translates
 * the ones a German company account actually sees and falls back to Swan's
 * value for the rest, which still beats an empty label.
 */
const label = (t: Translate, prefix: string, value: string) =>
  t(`pxtraCard:${prefix}_${value}`, { defaultValue: value });

/**
 * One required document. Swan accepts several kinds of file for the same
 * purpose, so the kind is chosen here rather than guessed from the first
 * entry, and a document Swan refused opens the zone again.
 */
export const DocumentDropZone: FC<Props> = ({ document, disabled, onUpload }) => {
  const { t } = useTranslation();
  const inputId = useId();
  const [documentType, setDocumentType] = useState(document.acceptable_types[0] ?? '');
  const [isOver, setIsOver] = useState(false);

  const refused = document.status === 'refused';
  const done = document.uploaded;

  const take = (file: File | undefined) => {
    if (file && !disabled) onUpload(documentType, file);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsOver(false);
    take(event.dataTransfer.files?.[0]);
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    take(event.target.files?.[0]);
    // Picking the same file twice in a row fires no change event otherwise.
    event.target.value = '';
  };

  const borderClass = () => {
    if (done) return 'border-success-main bg-white';
    if (refused) return 'border-error-main bg-error-lightest';
    if (isOver) return 'border-heaven-600 bg-heaven-100';

    return 'border-grey-200 bg-white';
  };

  return (
    <div className="mt-3">
      <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
        <p className="m-0 text-sm font-medium">{label(t, 'documentPurpose', document.name)}</p>
        {document.acceptable_types.length > 1 && !done && (
          <select
            className="box-border rounded-lg border border-solid border-grey-100 bg-white px-2 py-1 text-xs"
            value={documentType}
            disabled={disabled}
            aria-label={t('pxtraCard:documentsTypeLabel')}
            onChange={(event) => setDocumentType(event.target.value)}
          >
            {document.acceptable_types.map((type) => (
              <option key={type} value={type}>
                {label(t, 'documentType', type)}
              </option>
            ))}
          </select>
        )}
      </div>

      <div
        className={`flex flex-col items-center justify-center rounded-xl border border-dashed px-4 py-6 text-center transition-colors ${borderClass()}`}
        onDragOver={(event) => {
          event.preventDefault();
          setIsOver(true);
        }}
        onDragLeave={() => setIsOver(false)}
        onDrop={onDrop}
      >
        {done ? (
          <p className="m-0 flex items-center gap-1 text-sm text-success-main">
            <CheckmarkIcon className="h-4 w-4" />
            {t('pxtraCard:documentsUploaded')}
          </p>
        ) : (
          <>
            <p className="m-0 text-sm text-grey-600">
              {t('pxtraCard:documentsDropHere')}{' '}
              <label htmlFor={inputId} className="cursor-pointer text-heaven-600 underline">
                {t('pxtraCard:documentsChoose')}
              </label>
            </p>
            <p className="m-0 mt-1 text-xs text-grey-500">
              {document.acceptable_types.length === 1
                ? label(t, 'documentType', document.acceptable_types[0])
                : t('pxtraCard:documentsAccepted')}
            </p>
            <input
              id={inputId}
              type="file"
              className="hidden"
              accept=".pdf,.png,.jpg,.jpeg,.heic"
              disabled={disabled}
              onChange={onChange}
            />
          </>
        )}
      </div>

      {refused && document.refused_reason && (
        <p className="m-0 mt-1 text-xs text-error-main">
          {t('pxtraCard:documentsRefused', { reason: document.refused_reason })}
        </p>
      )}
    </div>
  );
};
